import { useState, useCallback, useEffect } from "react";

import { useObjectMemo } from "byzantine";
import ApiHttp from "byzantine/src/ApiHttp";

import { DOCUMENTS_PER_PAGE } from "../constants";

import type { WebDocument } from "byzantine/src/dbbl/businessLogic/entities/documents/types";

export const usePaginatedDocuments = (
  existingDocuments: WebDocument[],
  onBackendPaginationToggledOff: () => void
) => {
  const [currentMaxPage, setCurrentMaxPage] = useState(1);
  const [documents, setDocuments] = useState(existingDocuments);
  const isProbingForNextPageOnLoad = documents.length === DOCUMENTS_PER_PAGE;
  const [isApiPaginationActive, setIsApiPaginationActive] = useState(
    isProbingForNextPageOnLoad
  );

  const loadNextPage = useCallback(async () => {
    // Despite being named "after", the backend is actually "after" inclusive, so we add 1.
    const response = await ApiHttp.fetch(
      `documents?after=${currentMaxPage * DOCUMENTS_PER_PAGE + 1}`,
      {
        method: "GET",
      }
    );
    const newDocuments: WebDocument[] = response.documents;
    const firstExistingDocument = existingDocuments[0];
    const firstNewDocument = newDocuments[0];

    // Only matters on the first render
    if (firstExistingDocument?.id === firstNewDocument?.id) {
      // Documents are the same, pagination is not being respected, disable BE support.
      setIsApiPaginationActive(false);
      onBackendPaginationToggledOff();
    }

    setDocuments((prev) => [...prev, ...newDocuments]);

    // If we don't have a full page of documents back, then we know for sure there
    // are no more documents to load, so we can disable the "Load next page" functionality.
    if (newDocuments.length < DOCUMENTS_PER_PAGE) {
      setIsApiPaginationActive(false);
      onBackendPaginationToggledOff();
    } else {
      // If there are more documents, increment max page
      setCurrentMaxPage((prev) => prev + 1);
    }
  }, [currentMaxPage, existingDocuments, onBackendPaginationToggledOff]);

  // On mount determine if we need to load the next page of documents
  useEffect(() => {
    // If the `documents.length` we have in memory is equal to our `DOCUMENTS_PER_PAGE`, it
    // means pagination may be respected & we should try to load the next set of documents.
    if (isProbingForNextPageOnLoad) {
      // Note: If we wanted to support loading specific paginated pages on initial load, we'd need to
      // backfill the previous pages of documents here until we reach the current page defined via the
      // `page` query parameter.
      loadNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useObjectMemo({
    isApiPaginationActive,
    currentMaxPage,
    loadNextPage,
    documents,
  });
};
